import { useState } from 'react';
import { Globe2, PlugZap, BookOpenCheck, CheckCircle, AlertTriangle, ListFilter, Database } from 'lucide-react';
import { useCadStore } from '../store/cadStore';
import { useScrapeStore, accountId } from '../store/scrapeStore';
import { useThemeTokens } from '../theme';
import { submitUrlForTraining, checkLearningWorker } from '../services/learningService';
import { pingOllama } from '../services/embeddingService';

const inp = { padding: '8px 11px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 12.5, outline: 'none', width: '100%', boxSizing: 'border-box' };

const Row = ({ label, hint = '', children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#f1f5f9', letterSpacing: '0.02em' }}>{label}</span>
        {hint && <span style={{ fontSize: 11, color: '#64748b' }}>{hint}</span>}
        {children}
    </div>
);

/* Settings → AI Training: manual URL entry for continuous learning.
   URL → Cloudflare Worker scrape → local Ollama vectorize → Vectorize
   (account namespace) + D1 relational rows (account-scoped). */
export default function TrainingPanel() {
    const t = useThemeTokens();
    const workerBase = useScrapeStore((s) => s.workerBase);
    const ollamaBase = useScrapeStore((s) => s.ollamaBase);
    const embedModel = useScrapeStore((s) => s.embedModel);
    const connected = useScrapeStore((s) => s.connected);
    const testing = useScrapeStore((s) => s.testing);
    const training = useScrapeStore((s) => s.training);
    const setWorkerBase = useScrapeStore((s) => s.setWorkerBase);
    const setOllamaBase = useScrapeStore((s) => s.setOllamaBase);
    const setEmbedModel = useScrapeStore((s) => s.setEmbedModel);
    const activeProjectId = useCadStore((s) => s.activeProjectId);
    const [url, setUrl] = useState('');
    const [ollamaOk, setOllamaOk] = useState('unknown');

    const learn = async () => {
        if (!url.trim()) return;
        await submitUrlForTraining(url.trim(), activeProjectId);
        setUrl('');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 14, background: t.accentSoft, border: '1px solid #334155' }}>
                <span style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 600, lineHeight: 1.5 }}>
                    <Globe2 size={13} /> Continuous learning is <strong>account-scoped</strong> — every URL you teach is shared
                    across all projects in <strong>{accountId()}</strong>.
                </span>
            </div>

            <Row label="Cloudflare Worker URL" hint="The Scrape-to-CAD worker (see workers/). Applies to this account's knowledge base.">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input value={workerBase} onChange={(e) => setWorkerBase(e.target.value)} placeholder="https://roocad-learning.<subdomain>.workers.dev" style={{ ...inp, flex: 1 }} />
                    <button onClick={() => checkLearningWorker()} disabled={testing}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', cursor: 'pointer', background: '#1e293b', color: '#e2e8f0', fontWeight: 600, fontSize: 12 }}>
                        <PlugZap size={13} /> {testing ? 'Testing…' : 'Test'}
                    </button>
                </div>
                {connected && <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}><CheckCircle size={12} /> Cloudflare Worker reachable</span>}
                {!connected && testing === false && workerBase && <span style={{ fontSize: 11, color: '#f87171', fontWeight: 600 }}>Not reachable — deploy workers/ first.</span>}
            </Row>

            <Row label="Local Ollama Proxy Gateway" hint="Embedding engine (OpenAI-compatible /v1/embeddings).">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input value={ollamaBase} onChange={(e) => setOllamaBase(e.target.value)} style={{ ...inp, flex: 1 }} />
                    <button onClick={async () => setOllamaOk(await pingOllama() ? 'up' : 'down')}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #334155', cursor: 'pointer', background: '#1e293b', color: '#e2e8f0', fontSize: 11.5, fontWeight: 600 }}>
                        {ollamaOk === 'up' ? <CheckCircle size={12} /> : <PlugZap size={12} />} Ping
                    </button>
                </div>
                {ollamaOk === 'up' && <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600 }}>Ollama gateway is up.</span>}
                {ollamaOk === 'down' && <span style={{ fontSize: 11, color: '#f87171', fontWeight: 600 }}>Ollama not reachable — is the proxy gateway running?</span>}
            </Row>
            <Row label="Embedding Model" hint="Vector dimensions must match the roocad-knowledge Vectorize index.">
                <input value={embedModel} onChange={(e) => setEmbedModel(e.target.value)} style={inp} />
            </Row>
<Row label="Train From URL" hint="Scrape a technical page → vectorize → index into your account's knowledge base.">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && learn()} placeholder="https://example.com/part-spec" style={{ ...inp, flex: 1 }} />
                    <button onClick={learn} disabled={training.running || !url.trim()}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 9, cursor: 'pointer', border: 'none', fontWeight: 700, fontSize: 12.5, color: '#fff', background: 'linear-gradient(135deg,#2563eb,#06b6d4)', opacity: training.running || !url.trim() ? 0.5 : 1 }}>
                        <BookOpenCheck size={14} /> {training.running ? 'Learning…' : 'Learn'}
                    </button>
                </div>
                {training.message && <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600 }}><CheckCircle size={12} /> {training.message}</span>}
                {training.error && <span style={{ fontSize: 11, color: '#f87171', fontWeight: 600 }}><AlertTriangle size={12} /> {training.error}</span>}
            </Row>

            {training.urls.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}><ListFilter size={13} /> Trained URLs ({training.urls.length})</span>
                    {training.urls.map((u) => (
                        <div key={u.url} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, background: '#1e293b', border: '1px solid #334155' }}>
                            <Database size={12} color="#60a5fa" />
                            <span style={{ flex: 1, color: '#e2e8f0', fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.url}</span>
                            <span style={{ fontSize: 10, color: '#64748b' }}>{new Date(u.at).toLocaleDateString()}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
